// Third-party
import React from 'react';
import classNames from 'classnames';
import { useSelect, UseSelectState } from 'downshift';
import PerfectScrollbar from 'react-perfect-scrollbar';

// Utils
import { formatMessage } from '@/utils/i18n';

export interface SelectProps {
  items: Array<SelectItem>;
  children?: React.ReactElement;
  label: string;
  hideLabel?: boolean;
  onChange?: (changes: Partial<UseSelectState<SelectItem>>) => void;
  selectedItem?: SelectItem;
  disabled?: boolean;
  className?: string;
  suppressScrollX?: boolean;
  wheelPropagation?: boolean;
}

export interface SelectItem {
  value: string;
  label: React.ReactElement | string;
  disabled?: boolean;
}

/**
 * An accessible Select component that uses `downshift` hooks. Used for
 * allowing a user make a choice my selecting a single item. Not to be used
 * when clicking on an item should trigger an action, among other actions.
 */
const Select: React.FC<SelectProps> = props => {
  const { items, children, onChange, hideLabel = false, label, disabled, className, suppressScrollX, wheelPropagation = false } = props;

  const itemToString = (item: SelectItem) => (item ? item.value : '');
  const { isOpen, getLabelProps, getItemProps, getMenuProps, getToggleButtonProps, highlightedIndex, selectedItem } = useSelect({
    items,
    itemToString,
    onSelectedItemChange: onChange,
    selectedItem: props.selectedItem,
  });

  let button;
  const buttonProps = getToggleButtonProps({ disabled, type: 'button' });
  if (children) {
    button = React.cloneElement(children, buttonProps);
  } else {
    button = (
      <button {...buttonProps} className="select-button">
        {selectedItem?.label ?? formatMessage('Common.Select')}
        <i className="select-button-icon icon-cheveron-down" aria-hidden="true" />
      </button>
    );
  }

  const setListElements = () => {
    const { selectedItem } = props;

    if (!items.length && selectedItem?.value) {
      return (
        <li className={classNames('select-list-item', { active: true, disabled: true })} key={selectedItem.value}>
          {selectedItem.label}
        </li>
      );
    }

    if (!items.length && !selectedItem?.value) return <></>;

    return (
      <PerfectScrollbar options={{ suppressScrollX, wheelPropagation }}>
        {items.map((item, index) => (
          <li
            className={classNames('select-list-item', { active: highlightedIndex === index })}
            key={`${item.value}${index}`}
            {...getItemProps({ item, index, disabled: item.disabled })}
          >
            {item.label}
          </li>
        ))}
      </PerfectScrollbar>
    );
  };

  return (
    <div className={classNames('select-container', className)}>
      <label {...getLabelProps()} className={classNames('select-label', { 'visually-hidden': hideLabel })}>
        {label}
      </label>
      {button}
      <ul className="select-list" {...getMenuProps()}>
        {isOpen && setListElements()}
      </ul>
    </div>
  );
};

export default Select;
